/**
 * Pure helpers for inserting pandora text (descriptions, keywords, subtitles,
 * annotations) as timeline text clips. No DOM / store dependencies so they can
 * be unit-tested directly.
 */
import { clipEnd } from './helpers';
import type { Edit, PandoraVideoSource, TextStyle } from './types';

/** Style flavour per text source. */
export type TextPreset = 'title' | 'subtitle' | 'keyword';

export interface TextInsertStyle {
  style: TextStyle;
  position: { x: number; y: number };
  fadeIn: number;
  fadeOut: number;
  /** default clip duration when the source has no in/out span */
  duration: number;
}

const BASE_STYLE: TextStyle = {
  fontFamily: 'sans',
  fontSize: 0.06,
  color: '#ffffff',
  bold: true,
  italic: false,
  align: 'center',
  background: '',
  outline: '#000000',
  outlineWidth: 0.003,
};

/**
 * Per-preset defaults:
 *  - title/description: larger, bold, upper-lower centre (matches addTextClip).
 *  - subtitle: smaller, not bold, bottom-centre with a readable backing box.
 *  - keyword: medium chip-like, bottom-centre.
 */
export function presetStyle(preset: TextPreset): TextInsertStyle {
  switch (preset) {
    case 'subtitle':
      return {
        style: {
          ...BASE_STYLE,
          fontSize: 0.045,
          bold: false,
          background: 'rgba(0,0,0,0.5)',
          outlineWidth: 0.002,
        },
        position: { x: 0.5, y: 0.88 },
        fadeIn: 0.15,
        fadeOut: 0.15,
        duration: 3,
      };
    case 'keyword':
      return {
        style: { ...BASE_STYLE, fontSize: 0.05 },
        position: { x: 0.5, y: 0.85 },
        fadeIn: 0.3,
        fadeOut: 0.3,
        duration: 3,
      };
    case 'title':
    default:
      return {
        style: { ...BASE_STYLE, fontSize: 0.06 },
        position: { x: 0.5, y: 0.85 },
        fadeIn: 0.3,
        fadeOut: 0.3,
        duration: 4,
      };
  }
}

/**
 * First start time >= `desiredStart` at which a clip of length `duration` fits
 * without overlapping any of the given occupied intervals. Overlay tracks forbid
 * overlap, so rather than let normalization shove the clip to the track end we
 * pick the next free slot at/after the requested position.
 */
export function findFreeOverlayStart(
  intervals: { start: number; end: number }[],
  desiredStart: number,
  duration: number,
): number {
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  let start = Math.max(0, desiredStart);
  // Repeatedly bump past any interval that overlaps [start, start+duration).
  let moved = true;
  while (moved) {
    moved = false;
    for (const iv of sorted) {
      if (start < iv.end - 1e-9 && start + duration > iv.start + 1e-9) {
        start = iv.end;
        moved = true;
      }
    }
  }
  return start;
}

/** Occupied intervals on the first overlay track of an edit. */
export function overlayIntervals(edit: Edit): { start: number; end: number }[] {
  const track = edit.tracks.find((t) => t.kind === 'overlay');
  if (!track) return [];
  return track.clips.map((c) => ({ start: c.start, end: clipEnd(c) }));
}

/** Cumulative concat-time offset at the start of each part (0-based index). */
export function partOffsetsFromDurations(durations: number[]): number[] {
  const out: number[] = [];
  let acc = 0;
  for (const d of durations) {
    out.push(acc);
    acc += d;
  }
  return out;
}

/**
 * Map a time on an item's CONCATENATED timeline (as pandora annotation in/out
 * are expressed) to a time on the edit timeline, using a video clip of that same
 * item already placed on the timeline.
 *
 * Returns null when no clip of this item covers that moment — the caller then
 * falls back to inserting at the playhead.
 */
export function concatTimeToTimeline(
  edit: Edit,
  archiveKey: string,
  itemId: string,
  partOffsets: number[],
  concatT: number,
): number | null {
  // Which part contains concatT, and the part-relative (source) time.
  let part = -1;
  let local = 0;
  for (let p = partOffsets.length - 1; p >= 0; p--) {
    if (concatT >= partOffsets[p] - 1e-9) {
      part = p + 1; // parts are 1-based
      local = concatT - partOffsets[p];
      break;
    }
  }
  if (part < 0) return null;

  // Source ids of this item/part in the edit.
  const sourceIds = new Set<string>();
  for (const [id, s] of Object.entries(edit.sources)) {
    if (
      (s as PandoraVideoSource).kind === 'pandora-video' &&
      (s as PandoraVideoSource).archive === archiveKey &&
      (s as PandoraVideoSource).item === itemId &&
      (s as PandoraVideoSource).part === part
    ) {
      sourceIds.add(id);
    }
  }
  if (!sourceIds.size) return null;

  for (const track of edit.tracks) {
    if (track.kind !== 'video') continue;
    for (const clip of track.clips) {
      if (!sourceIds.has(clip.source)) continue;
      if (local >= clip.in - 1e-9 && local <= clip.out + 1e-9) {
        return clip.start + (local - clip.in) / clip.speed;
      }
    }
  }
  return null;
}
